import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Book, ArrowLeft, Trash, GlobeSimple, Lock, Article, Feather, BookOpen } from '@phosphor-icons/react';
import { toast } from 'sonner';
import GlobalHeader from '../components/GlobalHeader';

export default function CollectionDetail() {
  const { collectionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [col, setCol] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [collectionId]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/collections/${collectionId}`,
        { withCredentials: true }
      );
      setCol(res.data);
    } catch (e) {
      toast.error('Cannot open this collection');
      navigate('/collections');
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/collections/${collectionId}/items/${itemId}`,
        { withCredentials: true }
      );
      toast.success('Removed');
      load();
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  const isOwner = user && col && user.user_id === col.user_id;

  if (loading) {
    return (
      <div className="min-h-screen">
        <GlobalHeader />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }
  if (!col) return null;

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <Link to="/collections" data-testid="back-button" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
            <ArrowLeft size={16} /> All Collections
          </Link>

          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              {col.is_public ? <GlobeSimple size={14} className="text-muted-foreground" /> : <Lock size={14} className="text-muted-foreground" />}
              <span className="text-xs uppercase tracking-widest text-muted-foreground">{col.is_public ? 'Public' : 'Private'}</span>
              <span className="text-xs text-muted-foreground">· by {col.user_name || 'Anonymous'}</span>
            </div>
            <h2 className="text-5xl md:text-6xl tracking-tight mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }} data-testid="collection-name">
              {col.name}
            </h2>
            {col.description && <p className="text-lg text-muted-foreground max-w-3xl">{col.description}</p>}
            <p className="text-sm text-muted-foreground mt-3">
              {col.items?.length || 0} item{(col.items?.length || 0) !== 1 ? 's' : ''}
            </p>
          </div>

          {(!col.items || col.items.length === 0) ? (
            <div className="bg-card border border-border rounded-lg p-16 text-center" data-testid="empty-collection">
              <BookOpen size={48} weight="duotone" className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">This collection is empty.</p>
              {isOwner && (
                <p className="text-sm text-muted-foreground mt-2">Browse articles and works, then use "Save to collection".</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {col.items.map((it) => (
                <div key={`${it.item_type}-${it.item_id}`} className="bg-card border border-border rounded-lg p-4 flex items-center gap-4 hover:border-primary transition-colors" data-testid={`col-item-${it.item_id}`}>
                  <div className="w-16 h-16 shrink-0 bg-muted rounded overflow-hidden">
                    {it.cover ? (
                      <img src={it.cover} alt="" className="w-full h-full object-cover" />
                    ) : it.item_type === 'article' ? (
                      <div className="w-full h-full flex items-center justify-center"><Article size={24} weight="duotone" className="text-muted-foreground" /></div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Feather size={24} weight="duotone" className="text-muted-foreground" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-widest text-primary mb-0.5">{it.item_type}</div>
                    <Link
                      to={it.item_type === 'article' ? `/article/${it.item_id}` : '#'}
                      onClick={(e) => { if (it.item_type !== 'article') e.preventDefault(); }}
                      className="font-semibold hover:text-primary truncate block"
                    >
                      {it.title || 'Untitled'}
                    </Link>
                    {it.author_name && <p className="text-sm text-muted-foreground truncate">by {it.author_name}</p>}
                  </div>
                  {isOwner && (
                    <button
                      onClick={() => removeItem(it.item_id)}
                      data-testid={`remove-item-${it.item_id}`}
                      className="text-red-600 hover:bg-red-50 p-2 rounded-full transition-colors"
                      title="Remove"
                    >
                      <Trash size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
